import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import useTheme from '@mui/material/styles/useTheme'
import { CheckCircle } from '@mui/icons-material'

import { useImageLibrary } from '@/context/ImageLibraryProvider'
import UploadPlaceholderImg from '@/assets/upload_placeholder.svg'
import { getEnhancedTheme } from '@/helpers/theme'

import ImageWithSkeleton from './ImageWithSkeleton'

const UPLOAD_ONLY_VIEW = 'upload-only'

const FileUploader = ({
  initialImage,
  onFileAdded,
  onFileRemoved,
  variant = 'full',
  selected,
  name,
  isLineBreak
}) => {
  const [image, setImage] = useState(initialImage ?? null)
  const [hasImageLoaded, setHasImageLoaded] = useState(false)
  const { isLoaded, token, getToken } = useImageLibrary()
  const theme = useTheme()

  useEffect(() => {
    setImage(initialImage)
  }, [initialImage])

  const enhancedTheme = getEnhancedTheme(theme)

  const handleOpenImagePicker = (viewType) => {
    isLoaded
      ? StandAloneImageLibrary.init({
        token: token,
        data: {
          setReturnValue: (val) => {
            const imageUrl = val?.[0]?.original
            setImage(imageUrl)
            onFileAdded(imageUrl)
          },
          view: viewType
        },
        theme: enhancedTheme,
        callbacks: {
          refreshAccessToken: getToken,
        },
        selector: 'image-library-mfe',
      })
      : window.alert('Oops! Looks like we ran into an issue, you can add your image once you\'ve finished onboarding.')
  }

  const UploadedFile = () => {
    const textButtonSx = {
      minWidth: 0,
      px: 0,
      py: 0.5,
    }

    return (
      <Box className="flex items-start gap-4">
        <Box
          className="relative shrink-0"
          sx={{
            border: selected && hasImageLoaded ? '2px solid' : 'none',
            borderColor: theme.palette.primary.main,
            borderRadius: '5px',
            padding: selected && hasImageLoaded ? '1px !important' : '0',
          }}
        >
          <Box className='max-w-[130px] min-w-[100px]'>
            <ImageWithSkeleton
              onLoaded={() => setHasImageLoaded(true)}
              src={image ?? UploadPlaceholderImg}
            />
          </Box>

          {selected && hasImageLoaded && (
            <Box className='absolute flex justify-center items-center top-2 right-2 bg-white rounded-[20px] overflow-hidden'>
              <CheckCircle color='primary' />
            </Box>
          )}
        </Box>
        <Box className="flex min-w-0 flex-1 flex-col items-start gap-1">
          <Typography variant='subtitle2' sx={{ color: '#1A1A1A' }}>
            {name}
          </Typography>
          <Box className="flex flex-wrap items-center gap-x-3">
            <Button
              variant='text'
              color='primary'
              sx={textButtonSx}
              onClick={() => handleOpenImagePicker(UPLOAD_ONLY_VIEW)}
            >
              <Typography
                className='underline'
                component='span'
                variant='h6'
                sx={{ fontSize: 'inherit' }}
              >
                Edit
              </Typography>
            </Button>
            <Button
              variant='text'
              color='error'
              sx={textButtonSx}
              onClick={() => {
                setImage(null)
                onFileRemoved()
              }}
            >
              <Typography
                className='underline'
                component='span'
                variant='h6'
                sx={{ fontSize: 'inherit' }}
              >
                Remove
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }
  const FileUploadArea = () => {
    const isCompact = variant === 'compact'

    const dropperSurfaceClass =
      'border border-dashed border-[#C0C4CF] bg-[#F5F5F5] rounded-[10px]'

    const containerClasses = isCompact
      ? 'flex items-center gap-4 mt-0 cursor-pointer'
      : `mt-5 cursor-pointer px-8 py-6 flex flex-col items-center justify-center space-y-4 ${dropperSurfaceClass}`

    const imageWrapperClasses = isCompact
      ? `flex justify-center w-40 h-20 p-5 ${dropperSurfaceClass}`
      : 'text-gray-400'

    return (
      <Box
        className={containerClasses}
        onDragEnter={(e) => {
          e.preventDefault()

          const item = e.dataTransfer.items[0]
          if (!item || item.kind !== 'file') {
            return
          }

          handleOpenImagePicker(UPLOAD_ONLY_VIEW)
        }}
        onClick={() => handleOpenImagePicker(UPLOAD_ONLY_VIEW)}
      >
        <Box className={imageWrapperClasses}>
          <img
            src={UploadPlaceholderImg}
            width={80.74}
            height={60}
            alt='upload icon'
          />
        </Box>
        <Typography
          variant='body2'
          className='!font-medium'
          sx={{ color: '#1A1A1A', fontSize: '14px' }}
        >
          Drop your file here, or{' '}
          <Typography
            className={`!font-semibold ${isLineBreak ? 'block' : 'inline-block'} underline underline-offset-2`}
            sx={{ color: '#1A1A1A', fontSize: '14px' }}
            component='span'
          >
            Browse
          </Typography>
        </Typography>
      </Box>
    )
  }

  return !image ? <FileUploadArea /> : <UploadedFile />
}

FileUploader.propTypes = {
  onFileAdded: PropTypes.func,
}

export default FileUploader
